/**
 * Removes Stripe's PaymentIntent client secret from the address bar (issue #1732).
 *
 * THE LEAK: `Plans.tsx` confirms with `redirect: 'if_required'` and `return_url:
 * window.location.href`, so a redirect-based payment method returns to the page with
 * `?payment_intent=…&payment_intent_client_secret=…&redirect_status=…` (a 3-D Secure card
 * challenge is a dialog on the page and does not). Nothing in the SPA reads those parameters,
 * and nothing removed them either, so every later `/api/` request carried the secret in its
 * Referer, which nginx's `/api/` error line writes to the log. Removing them loses nothing: the
 * webhook grants the plan whatever the page does. But after such a return the page also shows no
 * "confirming your payment" state. The app starts afresh, and `Plans.tsx` polls the entitlement
 * only once `paid` is set, which happens only in `confirmPayment`'s `onSuccess`, in the page that
 * was left for the redirect. That gap predates #1732 and is not changed here (Refs #1761).
 *
 * WHAT IS REMOVED: only that parameter (owner decision D9 and the ruling of 2026-10-28/29).
 * The path, every other parameter in its order and raw encoding (the query is never re-encoded:
 * `URLSearchParams.toString()` would rewrite `%20` as `+`, among others), and the fragment stay
 * as they were, and the page does not reload: `history.replaceState` with the current state,
 * and only when something was removed.
 *
 * WHICH SPELLINGS: a parameter counts as the client secret when PHP would read its name as
 * `payment_intent_client_secret`, in any letter case — the rule the website's
 * `SentryClientSecretScrubber` (whose docblock has the detail, checked against PHP 8.5) and its
 * address-bar twin (`frontend-client/resources/views/partials/strip-stripe-client-secret.blade.php`)
 * apply. The name is percent-decoded once with `+` read as a space (a malformed `%` stays as it
 * is) and ends at a decoded NUL; leading spaces are skipped; a space or `.` reads as `_`; a `[`
 * with a `]` anywhere after it ends the name (an array), while a `[` with no `]` after it reads as
 * `_`, as does every space, `.` and `[` after it. So `payment[intent_client_secret` and
 * `+payment_intent_client_secret` are removed, and `payment[intent]_client_secret` (the array
 * `payment`), `client_secret`, `xpayment_intent_client_secret` and every other name are kept.
 */

const KEY = 'payment_intent_client_secret';

/** The name PHP would register for this raw query key, lower-cased. */
function phpKeyName(rawName: string): string {
  let name = rawName
    .replace(/\+/g, ' ')
    .replace(/%([0-9a-fA-F]{2})/g, (_match, hex: string) => String.fromCharCode(parseInt(hex, 16)))
    .toLowerCase();
  const nul = name.indexOf('\u0000');
  if (nul !== -1) name = name.slice(0, nul);
  name = name.replace(/^ +/, '');

  const bracket = name.indexOf('[');
  if (bracket === -1) return name.replace(/[ .]/g, '_');
  if (bracket === 0) return ''; // PHP drops a variable whose name is empty before its first `[`.

  const base = name.slice(0, bracket).replace(/[ .]/g, '_');
  if (name.indexOf(']', bracket + 1) !== -1) return base;

  return `${base}_${name.slice(bracket + 1).replace(/[ .[]/g, '_')}`;
}

/**
 * The URL without the client secret, or `null` when it carries none. Only the query between the
 * first `?` and the fragment is read; every byte that is not a removed pair is kept.
 */
export function withoutClientSecret(href: string): string | null {
  const hashAt = href.indexOf('#');
  const head = hashAt === -1 ? href : href.slice(0, hashAt);
  const hash = hashAt === -1 ? '' : href.slice(hashAt);
  const queryAt = head.indexOf('?');
  if (queryAt === -1) return null;

  const pairs = head.slice(queryAt + 1).split('&');
  const kept = pairs.filter((pair) => {
    const equals = pair.indexOf('=');
    return phpKeyName(equals === -1 ? pair : pair.slice(0, equals)) !== KEY;
  });
  if (kept.length === pairs.length) return null;

  const query = kept.join('&');
  return `${head.slice(0, queryAt)}${query === '' ? '' : `?${query}`}${hash}`;
}

/**
 * Strips the client secret from the current address, in place. Call it before the router is
 * created: `createBrowserRouter` reads `window.location` when it starts and runs the first
 * route's loaders at once, so the router and every request it makes see the clean URL, and the
 * router's history never holds the secret. Never throws.
 */
export function stripClientSecretFromLocation(win: Pick<Window, 'location' | 'history'> = window): void {
  try {
    const clean = withoutClientSecret(String(win.location.href));
    if (clean === null) return;
    win.history.replaceState(win.history.state, '', clean);
  } catch {
    // A page must load whatever the browser does with the history API.
  }
}
